import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { WhatsappMessage } from '../types';

const WhatsappMessages = () => {
  const [messages, setMessages] = useState<WhatsappMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [messageTypeFilter, setMessageTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchMessages = async (
    search: string = '',
    page: number = 1,
    messageType: string = '',
    status: string = '',
    startDate: string = '',
    endDate: string = ''
  ) => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams();
      if (search) query.append('customer_search', search);
      if (messageType) query.append('message_type', messageType);
      if (status) query.append('status', status);
      if (startDate) query.append('start_date', startDate);
      if (endDate) query.append('end_date', endDate);
      query.append('page', String(page));
      query.append('per_page', '10');

      const res = await fetchWithAuth(`/whatsapp_messages?${query.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('jwt')}`
        }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await res.json();
      setMessages(data.whatsapp_messages);
      setCurrentPage(data.pagination.current_page);
      setTotalPages(data.pagination.total_pages);
      setTotalCount(data.pagination.total_count);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(searchTerm, currentPage, messageTypeFilter, statusFilter, startDate, endDate);
  }, [searchTerm, currentPage, messageTypeFilter, statusFilter, startDate, endDate]);

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  const getStatusBadge = (message: WhatsappMessage) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    
    switch (message.status) {
      case 'successful':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Successful</span>;
      case 'failed':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Failed</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Unknown</span>;
    }
  };

  const formatMessageType = (messageType: string) => {
    const typeMap: { [key: string]: string } = {
      'submitted': 'Order Submitted',
      'paid': 'Order Paid',
      'items_purchased': 'Items Purchased',
      'sa_warehouse': 'At SA Warehouse',
      'ready_for_collection': 'Ready for Collection',
      'collected': 'Order Collected',
      'invoice_ready': 'Invoice Ready',
      'otp': 'OTP Verification',
      'pickup_point_order_collection': 'Pickup Point Collection Code Check',
      'pickup_point_order_collection_failure': 'Pickup Point Collection Code Check Failure',
      'pickup_point_order_collection_failure_error': 'Pickup Point Collection Code Check Error'
    };
    
    return typeMap[messageType] || messageType;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Message History</h1>
        <p className="text-gray-600">Track all messages sent to customers</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Customer Search
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name or customer code..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="messageType" className="block text-sm font-medium text-gray-700 mb-1">
              Message Type
            </label>
            <select
              id="messageType"
              value={messageTypeFilter}
              onChange={(e) => setMessageTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="otp">OTP Verification</option>
              <option value="submitted">Order Submitted</option>
              <option value="paid">Order Paid</option>
              <option value="items_purchased">Items Purchased</option>
              <option value="sa_warehouse">At SA Warehouse</option>
              <option value="ready_for_collection">Ready for Collection</option>
              <option value="collected">Order Collected</option>
              <option value="invoice_ready">Invoice Ready</option>
              <option value="pickup_point_order_collection">Pickup Point Collection Code Check</option>
              <option value="pickup_point_order_collection_failure">Pickup Point Collection Code Check Failure</option>
              <option value="pickup_point_order_collection_failure_error">Pickup Point Collection Code Check Error</option>
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="successful">Successful</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Results count */}
        {totalCount > 0 && (
          <p className="text-sm text-gray-600">
            Found {totalCount} message{totalCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Messages Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="hidden md:table-cell px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Phone
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    Loading messages...
                  </td>
                </tr>
              ) : messages.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No messages found
                  </td>
                </tr>
              ) : (
                messages.map((message) => (
                  <tr key={message.id} className="hover:bg-gray-50">
                    <td className="hidden md:table-cell px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {message.sent_at ? formatDateTime(message.sent_at) : formatDateTime(message.created_at)}
                    </td>
                    <td className="px-2 md:px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {message.customer_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {message.customer_code}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 md:px-6 py-4 text-xs md:text-sm text-gray-900">
                      {formatMessageType(message.message_type)}
                    </td>
                    <td className="px-2 md:px-6 py-4">
                      {getStatusBadge(message)}
                    </td>
                    <td className="px-2 md:px-6 py-4 text-xs md:text-sm text-gray-500 whitespace-nowrap">
                      {message.phone_number}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsappMessages;
